<script setup lang="ts">
/**
 * BaseSidePanel — выезжающая сбоку панель (slide-over) для overlay-контента.
 *
 * Окно на OverlayDialog с затемнением и панелью, прибитой к правому (по умолчанию)
 * или левому краю. Закрытие по клику на фон и по Esc. Используется для карточки клиента
 * в мессенджере на узких экранах (<1280), где постоянной третьей колонки нет, для
 * мобильной шторки меню (`side="left"`) и для плашки локации на мобильном
 * (`side="bottom"`: на всю ширину, до 85% высоты, выезд снизу).
 */
import { computed } from 'vue';

import { IconCross } from '@/shared/ui/icons';
import { OverlayDialog } from '@/shared/ui/overlay';

interface Props {
  open: boolean;
  width?: string;
  /** Край экрана, от которого выезжает панель. */
  side?: 'bottom' | 'left' | 'right';
  /** Имя диалога для скринридера: заголовок в шапке — разметка, а не подпись роли. */
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  width: '320px',
  side: 'right',
  label: undefined,
});

const emit = defineEmits<{
  close: [];
}>();

const isBottom = computed(() => props.side === 'bottom');
const panelTransition = computed(() => {
  const offscreenClass = { bottom: 'translate-y-full', left: '-translate-x-full', right: 'translate-x-full' }[
    props.side
  ];
  const onscreenClass = isBottom.value ? 'translate-y-0' : 'translate-x-0';

  return {
    enterActiveClass: 'transition-transform duration-300 ease-out',
    leaveActiveClass: 'transition-transform duration-200 ease-in',
    enterFromClass: offscreenClass,
    enterToClass: onscreenClass,
    leaveFromClass: onscreenClass,
    leaveToClass: offscreenClass,
  };
});
const placementClass = computed(
  () =>
    ({
      bottom: 'inset-x-0 bottom-0 max-h-[85dvh] w-full rounded-t-2xl',
      left: 'top-0 left-0 h-full pt-[env(safe-area-inset-top,0px)]',
      right: 'top-0 right-0 h-full pt-[env(safe-area-inset-top,0px)]',
    })[props.side],
);
</script>

<template>
  <OverlayDialog
    :open="open"
    backdrop-class="bg-black/50"
    :transition="panelTransition"
    :aria-label="label"
    @request-close="emit('close')"
    @backdrop-click="emit('close')"
  >
    <aside
      class="fixed flex max-w-full flex-col bg-(--base-container-bg) shadow-xl"
      :class="placementClass"
      :style="{ width: isBottom ? undefined : width }"
    >
      <div class="flex shrink-0 items-center gap-2 border-b border-(--base-popup-border-color) px-4 py-3">
        <div class="min-w-0 flex-1">
          <slot name="header" />
        </div>
        <button
          type="button"
          class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-(--base-popup-secondary-color) transition-colors hover:bg-(--base-secondary-color) hover:text-(--base-text-color)"
          aria-label="Закрыть"
          @click="emit('close')"
        >
          <IconCross
            :width="20"
            :height="20"
          />
        </button>
      </div>

      <div class="scrollbar min-h-0 flex-1 overflow-y-auto">
        <slot />
      </div>

      <div
        v-if="$slots.footer"
        class="shrink-0 border-t border-(--base-popup-border-color) px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6"
      >
        <slot name="footer" />
      </div>
    </aside>
  </OverlayDialog>
</template>
